import pitchWorkletUrl from '@tunasong/audio-dsp/pitch.worklet?url'
import type { IAudioContext } from 'standardized-audio-context'
import invariant from 'tiny-invariant'

/**
 * `registerProcessor` throws when the same processor name is registered twice, so the module
 * load has to be shared by every consumer in the page rather than owned by whichever one got
 * there first. `DSPController` (the `pitch` DSP feature) and `PitchCapture` (the Pitch Trainer's
 * own microphone) both load it, and a user who opens the tuner and the trainer in one session
 * must not be the case that breaks.
 *
 * Keyed by context, not a single module-level promise: a second `AudioContext` — a fresh engine
 * after a device change — has its own worklet registry, and a shared promise would report the
 * module loaded there when it was not. `new AudioWorkletNode` then fails with `unknown
 * processor` and nothing in the DOM says why.
 */
const loaded = new WeakMap<IAudioContext, Promise<void>>()

export const loadPitchWorklet = (context: IAudioContext): Promise<void> => {
  const existing = loaded.get(context)
  if (existing) {
    return existing
  }
  const { audioWorklet } = context
  invariant(audioWorklet, 'Audio Context does not support audio worklets')
  const promise = audioWorklet.addModule(pitchWorkletUrl)
  loaded.set(context, promise)
  return promise
}
